import React, { useState } from 'react';
import {
  deleteOwnAccountConfirmedEmail,
  restoreOwnAccount,
  signOut,
  type PendingAccountDeletion,
} from '../../services/authService';

type Props = {
  pending: PendingAccountDeletion;
  accountEmail: string;
  onRestored: () => void;
  onSignedOut: () => void;
};

function formatKoreanDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 탈퇴 대기(14일 유예) 계정으로 로그인했을 때 앱 대신 보여 주는 복구/즉시삭제 화면 */
export default function PendingAccountDeletionScreen({ pending, accountEmail, onRestored, onSignedOut }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [typedEmail, setTypedEmail] = useState('');
  const email = accountEmail.trim().toLowerCase();

  const handleRestore = async () => {
    setBusy(true);
    setError('');
    try {
      await restoreOwnAccount();
      onRestored();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '계정 복구에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteNow = async () => {
    if (!email || typedEmail.trim().toLowerCase() !== email) {
      setError('가입 시 사용한 이메일과 정확히 일치하게 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await deleteOwnAccountConfirmedEmail(email, 'immediate');
      await signOut().catch(() => undefined);
      onSignedOut();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '영구삭제에 실패했습니다.');
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    setBusy(true);
    await signOut().catch(() => undefined);
    onSignedOut();
  };

  return (
    <div className="cp-modal-backdrop cp-auth-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="cp-pending-deletion-title">
      <div className="cp-modal cp-auth-modal">
        <h2 id="cp-pending-deletion-title" className="cp-auth-modal-title">
          탈퇴 신청된 계정입니다
        </h2>
        <p className="cp-auth-modal-hint">
          {formatKoreanDate(pending.requestedAt)}에 탈퇴를 신청하셨습니다.{' '}
          <strong>{formatKoreanDate(pending.purgeAfter)}</strong> 이후 계정과 모든 데이터가 영구 삭제되며, 그 전까지는
          복구할 수 있습니다.
        </p>
        {deleteOpen ? (
          <div className="cp-auth-modal-form">
            <label className="cp-auth-modal-label" htmlFor="cp-pending-deletion-email">
              지금 바로 영구삭제하려면 가입 이메일을 입력해 주세요. 삭제 후에는 복구할 수 없습니다.
            </label>
            <input
              id="cp-pending-deletion-email"
              type="email"
              autoComplete="off"
              className="cp-auth-modal-input"
              value={typedEmail}
              onChange={(e) => setTypedEmail(e.target.value)}
              disabled={busy}
            />
          </div>
        ) : null}
        {error && <p className="cp-auth-modal-error">{error}</p>}
        <div className="cp-modal-footer cp-leave-modal-actions">
          {deleteOpen ? (
            <>
              <button type="button" className="cp-btn-secondary" disabled={busy} onClick={() => setDeleteOpen(false)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                style={{ background: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
                disabled={busy || !email || typedEmail.trim().toLowerCase() !== email}
                onClick={() => void handleDeleteNow()}
              >
                {busy ? '처리 중…' : '즉시 영구삭제'}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="cp-btn-secondary" disabled={busy} onClick={() => void handleSignOut()}>
                로그아웃
              </button>
              <button type="button" className="cp-btn-secondary" disabled={busy} onClick={() => setDeleteOpen(true)}>
                지금 영구삭제
              </button>
              <button type="button" className="cp-btn-primary" disabled={busy} onClick={() => void handleRestore()}>
                {busy ? '처리 중…' : '계정 복구'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
